import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { RateioResponse } from '@triade/contracts';
import { EllipsisVertical } from 'lucide-react';
import { useRateio, useRateiosResumo, useRecalcularRateio, useExcluirRateio, useSalvarRateio } from '@/api/rateios';
import { usePode } from '@/api/me';
import { ErroLista } from '@/components/cargos';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { MonthPicker, type MarcaMes } from '@/components/ui/month-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ehZero, formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { AbaCifs } from './aba-cifs';
import { AbaConferencia } from './aba-conferencia';
import { AbaMapa } from './aba-mapa';
import { AbaCustoMinuto } from './aba-custo-minuto';
import { AbaDepreciacoes } from './aba-depreciacoes';
import { AbaSalarios } from './aba-salarios';
import { CardTotal, mensagemApi, type TomCard } from './comum';
import { ReabrirRateioDialog } from './reabrir-rateio-dialog';
import { BotaoRelatorios } from '@/relatorios';

/** Palavra que o usuario precisa digitar para confirmar a exclusao do rateio. */
const PALAVRA_EXCLUIR = 'EXCLUIR';

export const ABAS_RATEIO = ['cifs', 'salarios', 'depreciacoes', 'mapa', 'custo-minuto', 'conferencia'] as const;
export type AbaRateio = (typeof ABAS_RATEIO)[number];

/** Abas de tabela larga (mapa, CIFs, Salarios, Depreciacoes): ocupam a largura total da tela, sem o container de 1100px. */
const ABAS_LARGURA_TOTAL: readonly AbaRateio[] = ['cifs', 'salarios', 'depreciacoes', 'mapa'];

const ROTULO_ABA: Record<AbaRateio, string> = {
  cifs: 'CIFs',
  salarios: 'Salários',
  depreciacoes: 'Depreciações',
  mapa: 'Mapa de rateio',
  'custo-minuto': 'Custo minuto',
  conferencia: 'Conferência',
};

/** Busca da URL: `comp` = AAAA-MM (padrao = mes anterior ao atual) e `rateioAba`. Valor invalido e descartado. */
export interface BuscaRateios {
  comp?: string;
  rateioAba?: AbaRateio;
}

export function lerBuscaRateios(bruto: Record<string, unknown>): BuscaRateios {
  const comp = typeof bruto.comp === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(bruto.comp) ? bruto.comp : undefined;
  const rateioAba = ABAS_RATEIO.find((a) => a === bruto.rateioAba);
  return {
    ...(comp ? { comp } : {}),
    ...(rateioAba ? { rateioAba } : {}),
  };
}

/** Mes anterior ao atual (AAAA-MM): a competencia padrao da tela de rateios. */
export function competenciaAnterior(agora: Date = new Date()): string {
  const d = new Date(agora.getFullYear(), agora.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const BOTAO = 'flex h-[38px] items-center gap-2 rounded-[10px] border border-border bg-card px-3.5 font-medium hover:bg-line-soft disabled:opacity-60';

const STATUS: Record<RateioResponse['status'], { rotulo: string; classe: string }> = {
  SEM_RATEIO: { rotulo: 'Sem rateio', classe: 'bg-line-soft text-ink-secondary' },
  ABERTO: { rotulo: 'Aberto — não salvo', classe: 'bg-warning-bg text-warning-ink' },
  FECHADO: { rotulo: 'Fechado', classe: 'bg-success-bg text-success-ink' },
};

/** Tela "Rateios" (/custos/rateios): calcula, salva, exclui e exporta o rateio da competencia; abas com os detalhes. */
export function RateiosPage({ busca }: { busca: BuscaRateios }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rateio.editar');
  const verCusto = pode('custo.ver');
  const comp = busca.comp ?? competenciaAnterior();
  const rateio = useRateio(comp);
  const resumos = useRateiosResumo();
  /** Conferencia do CPM: so com o sinalizador da API ligado; R$ (custo.ver) + rateio.ver. Sem isso a aba some e a URL cai na primeira aba. */
  const conferenciaAtiva = rateio.data?.conferenciaCpmAtiva === true;
  const verConferencia = conferenciaAtiva && verCusto && pode('rateio.ver');
  const rateioAba: AbaRateio = busca.rateioAba && (busca.rateioAba !== 'conferencia' || verConferencia) ? busca.rateioAba : 'cifs';

  const mudar = (mudanca: BuscaRateios) => {
    void navigate({ to: '/custos/rateios', search: (anterior) => ({ ...anterior, ...mudanca }), replace: true });
  };

  const recalcular = useRecalcularRateio();
  const salvar = useSalvarRateio();
  const excluir = useExcluirRateio();
  const exportacao = useExportar();
  // Pontos do seletor de mes: so rateio fechado (salvo no app ou importado do XARP).
  const marcadores = useMemo(() => {
    const m: Record<string, MarcaMes> = {};
    for (const x of resumos.data ?? []) if (x.status === 'FECHADO') m[x.competencia] = x.origem === 'XARP' ? 'XARP' : 'APP';
    return m;
  }, [resumos.data]);
  const [confirmarExcluir, setConfirmarExcluir] = useState(false);
  const [digitadoExcluir, setDigitadoExcluir] = useState('');
  const [reabrirAberto, setReabrirAberto] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const r = rateio.data;
  const ocupado = recalcular.isPending || salvar.isPending || excluir.isPending;
  const status = r?.status;
  const semRateio = status === 'SEM_RATEIO';
  // Recalcular: nao se mexe num rateio fechado. Salvar: so o calculado e ainda nao salvo. Excluir: so se existir.
  const podeRecalcular = !ocupado && r !== undefined && status !== 'FECHADO';
  const podeSalvar = !ocupado && status === 'ABERTO' && r?.calculadoEm != null;
  const podeReabrir = !ocupado && status === 'FECHADO';
  const podeExcluir = !ocupado && r !== undefined && !semRateio;

  const fecharExcluir = () => {
    setConfirmarExcluir(false);
    setDigitadoExcluir('');
  };
  const podeExportar = verCusto && r !== undefined && !semRateio && !exportacao.exportando;

  const rodarAcao = (acao: () => void) => {
    setErroAcao(null);
    acao();
  };

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os rateios.</Mensagem>;
  else if (rateio.isError && !r) corpo = <div className="p-4"><ErroLista erro={rateio.error} onTentar={() => void rateio.refetch()} titulo="Não foi possível carregar o rateio" /></div>;
  else if (!r) corpo = <Mensagem papel="status">Carregando rateio…</Mensagem>;
  else if (semRateio) corpo = <Mensagem papel="status">Nenhum rateio nesta competência. Clique em Recalcular.</Mensagem>;
  else {
    corpo = (
      <Tabs value={rateioAba} onValueChange={(v) => mudar({ rateioAba: v as AbaRateio })} className="gap-4">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          {ABAS_RATEIO.filter((a) => a !== 'conferencia' || verConferencia).map((a) => (
            <TabsTrigger key={a} value={a}>
              {ROTULO_ABA[a]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="cifs">
          <AbaCifs rateio={r} />
        </TabsContent>
        <TabsContent value="salarios">
          <AbaSalarios rateio={r} />
        </TabsContent>
        <TabsContent value="depreciacoes">
          <AbaDepreciacoes rateio={r} />
        </TabsContent>
        <TabsContent value="mapa">
          <AbaMapa rateio={r} />
        </TabsContent>
        <TabsContent value="custo-minuto">
          <AbaCustoMinuto rateio={r} />
        </TabsContent>
        {verConferencia && (
          <TabsContent value="conferencia">
            <AbaConferencia competencia={comp} podeEditar={podeEditar} />
          </TabsContent>
        )}
      </Tabs>
    );
  }

  return (
    <div data-testid="rateios-pagina" className={cn('flex w-full flex-col gap-5 pb-4', ABAS_LARGURA_TOTAL.includes(rateioAba) && r && !semRateio ? '' : 'mx-auto max-w-[1100px]')}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Rateios
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Rateios</h1>
          <p className="text-small text-ink-secondary">Rateio dos custos indiretos e custo por minuto</p>
        </div>
        <BotaoRelatorios tela="rateios" params={{ competencia: comp, conferencia: conferenciaAtiva ? '1' : undefined }} />
      </div>

      {(podeEditar || verCusto) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {podeEditar && (
              <>
                <Button type="button" disabled={!podeRecalcular} onClick={() => rodarAcao(() => recalcular.mutate({ competencia: comp }, { onError: (e) => setErroAcao(mensagemApi(e)) }))}>
                  {recalcular.isPending ? 'Recalculando…' : 'Recalcular'}
                </Button>
                <Button type="button" variant="secondary" disabled={!podeSalvar} onClick={() => rodarAcao(() => salvar.mutate({ competencia: comp }, { onError: (e) => setErroAcao(mensagemApi(e)) }))}>
                  {salvar.isPending ? 'Salvando…' : 'Salvar rateio'}
                </Button>
                {status === 'FECHADO' && (
                  <Button type="button" variant="secondary" disabled={!podeReabrir} onClick={() => setReabrirAberto(true)}>
                    Reabrir rateio
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" aria-label="Mais ações" disabled={ocupado} className="px-3">
                      <EllipsisVertical aria-hidden="true" className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[200px] rounded-xl border-border p-1.5">
                    <DropdownMenuItem disabled={!podeExcluir} onSelect={() => setConfirmarExcluir(true)} className="text-destructive focus:text-destructive">
                      Excluir rateio…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
          {verCusto && (
            <BotaoExportar
              rotulo="Exportar Excel"
              exportando={exportacao.exportando}
              desabilitado={!podeExportar}
              onClick={() => void exportacao.exportar(`/rateios/${comp}/exportar.xlsx`)}
            />
          )}
        </div>
      )}

      <ErroExportacao erro={exportacao.erro} />
      {erroAcao && (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          Não foi possível concluir a ação: {erroAcao}
        </div>
      )}

      <section aria-label="Opções do rateio" className="flex flex-col gap-4">
        <h2 className="text-title font-semibold">Opções</h2>
        <div className="flex flex-wrap items-end gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="rateio-comp" className="text-small font-medium text-ink-secondary">Mês de análise</label>
            <MonthPicker id="rateio-comp" aria-label="Mês de análise" value={comp} onChange={(c) => mudar({ comp: c })} marcadores={marcadores} legenda />
          </div>
          {r && (
            <span data-testid="status-rateio" className={`rounded-full px-3 py-1 text-small font-semibold ${STATUS[r.status].classe}`}>
              {STATUS[r.status].rotulo}
            </span>
          )}
        </div>

        {r?.avisos && r.avisos.length > 0 && (
          <div role="status" className="flex flex-col gap-1 rounded-xl border border-warning-ink/30 bg-warning-bg px-4 py-3 text-small text-warning-ink">
            {r.avisos.map((a) => (
              <p key={a.codigo}>{a.mensagem}</p>
            ))}
          </div>
        )}

        {r?.totais && (
          <div className="flex flex-wrap gap-3">
            <CardTotal testid="card-despesas" rotulo="Despesas" valor={formatarMoeda(r.totais.despesas)} tom="sucesso" />
            <CardTotal testid="card-custo-indireto" rotulo="Custo indireto" valor={formatarMoeda(r.totais.custoIndireto)} tom="alerta" />
            <CardTotal testid="card-nao-alocado" rotulo="Não alocado" valor={formatarMoeda(r.totais.naoAlocado)} tom={tomNaoAlocado(r.totais.naoAlocado)} />
          </div>
        )}
      </section>

      <section aria-label="Análises do rateio" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-title font-semibold">Análises</h2>
        {corpo}
      </section>

      <ReabrirRateioDialog competencia={comp} aberto={reabrirAberto} onFechar={() => setReabrirAberto(false)} />

      <AlertDialog open={confirmarExcluir} onOpenChange={(o) => !excluir.isPending && (o ? setConfirmarExcluir(true) : fecharExcluir())}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o rateio de {comp}?</AlertDialogTitle>
            <AlertDialogDescription>
              O cálculo desta competência é apagado. Se o rateio já foi salvo, o custo por minuto gravado por ele também é apagado. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="excluir-rateio-confirmacao" className="text-small font-medium text-ink-secondary">
              Para confirmar, digite {PALAVRA_EXCLUIR}
            </label>
            <Input id="excluir-rateio-confirmacao" value={digitadoExcluir} onChange={(e) => setDigitadoExcluir(e.target.value)} autoComplete="off" disabled={excluir.isPending} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={digitadoExcluir !== PALAVRA_EXCLUIR || excluir.isPending}
              onClick={(e) => {
                e.preventDefault(); // o dialogo fecha so quando a exclusao der certo; erro mantem aberto
                setErroAcao(null);
                excluir.mutate(
                  { competencia: comp },
                  {
                    onSuccess: () => fecharExcluir(),
                    onError: (err) => {
                      fecharExcluir();
                      setErroAcao(mensagemApi(err));
                    },
                  },
                );
              }}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir rateio'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Nao alocado vermelho so quando ha valor (zero = neutro). Sinal lido da string; nenhuma conta. */
function tomNaoAlocado(valor: string): TomCard {
  return ehZero(valor) || valor.trim().startsWith('-') ? 'neutro' : 'erro';
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
